import { z } from 'zod';
import { Uuid } from './analytics.js';
import { Ulid } from './common.js';
/**
 * The player profile's own registry (`docs/achievements-plan.md` §12a.6, §12a.14): the level a
 * person's points come to, the trophy each band of five levels shows, and the looks BoardFlight
 * itself offers — pictures, backdrops, frames and backgrounds — with what opens each.
 *
 * This is the only list. The API checks what a person equips against it, the library draws from
 * it, and a test holds every `file` to a picture that exists under `apps/web/public/profile/`. A
 * picture is never replaced in place: a new one is a new file name here (`scripts/profile-art/SOURCE.md`).
 *
 * Nothing here is stored per person. A level, a trophy and what a level opens are read off the
 * points every time (§12a.6, *derived, not granted*), so unlinking a game lowers them honestly.
 */
/** Where the pictures are served from, on the library's host. */
export const PROFILE_ART_BASE = '/profile/';
/** Points needed to *reach* level `level` (§12a.6): (L − 1)(50 + 5L). Level 1 is 0. */
export function pointsForLevel(level) {
    const l = Math.max(1, Math.floor(level));
    return (l - 1) * (50 + 5 * l);
}
/** The level `points` come to, and how far into it they are (§12a.14.1). */
export function levelForPoints(points) {
    const p = Math.max(0, Math.floor(Number.isFinite(points) ? points : 0));
    // Solve (L − 1)(50 + 5L) ≤ p for the largest L, then correct for rounding either way.
    let level = Math.max(1, Math.floor((-45 + Math.sqrt(2025 + 20 * (p + 50))) / 10));
    while (pointsForLevel(level + 1) <= p)
        level += 1;
    while (level > 1 && pointsForLevel(level) > p)
        level -= 1;
    const from = pointsForLevel(level);
    return { level, into: p - from, span: pointsForLevel(level + 1) - from };
}
/** Eleven trophies, one per five levels, in the frames' metals (§12a.14.2). Lowest first. */
export const PROFILE_LEVEL_BANDS = [
    {
        from: 1,
        name: 'Wooden cup',
        trophy: 'trophies/level-01.webp',
        tint: '#b98a52',
    },
    {
        from: 5,
        name: 'Bronze cup',
        trophy: 'trophies/level-05.webp',
        tint: '#b8683a',
    },
    {
        from: 10,
        name: 'Silver cup',
        trophy: 'trophies/level-10.webp',
        tint: '#9aa3ad',
    },
    {
        from: 15,
        name: 'Silver cup with laurel',
        trophy: 'trophies/level-15.webp',
        tint: '#aab3bd',
    },
    {
        from: 20,
        name: 'Gold cup',
        trophy: 'trophies/level-20.webp',
        tint: '#d39a1c',
    },
    {
        from: 25,
        name: 'Gold cup with a gem',
        trophy: 'trophies/level-25.webp',
        tint: '#dca62a',
    },
    {
        from: 30,
        name: 'Winged gold cup',
        trophy: 'trophies/level-30.webp',
        tint: '#e4b234',
    },
    {
        from: 35,
        name: 'Platinum cup',
        trophy: 'trophies/level-35.webp',
        tint: '#8fb2cc',
    },
    {
        from: 40,
        name: 'Platinum cup with a gem',
        trophy: 'trophies/level-40.webp',
        tint: '#9cbfd8',
    },
    {
        from: 45,
        name: 'Crowned platinum cup',
        trophy: 'trophies/level-45.webp',
        tint: '#a9cbe3',
    },
    {
        from: 50,
        name: 'Prism cup',
        trophy: 'trophies/level-50.webp',
        tint: '#9a6cff',
    },
];
/** The band `level` falls in. */
export function levelBand(level) {
    let band = PROFILE_LEVEL_BANDS[0];
    for (const b of PROFILE_LEVEL_BANDS)
        if (level >= b.from)
            band = b;
    return band;
}
/** The next band above `level`, or null at the top. */
export function nextLevelBand(level) {
    return PROFILE_LEVEL_BANDS.find((b) => b.from > level) ?? null;
}
/** The 16 pictures everybody has from the start (§12a.3, decision 18). The first is the default. */
export const PROFILE_AVATARS = [
    ['meeple', 'Meeple'],
    ['headphones', 'Meeple with headphones'],
    ['die', 'Die'],
    ['pawn', 'Pawn'],
    ['tile', 'Tile'],
    ['fox', 'Fox'],
    ['owl', 'Owl'],
    ['cat', 'Cat'],
    ['frog', 'Frog'],
    ['robot', 'Robot'],
    ['dragon', 'Dragon'],
    ['penguin', 'Penguin'],
    ['knight', 'Knight'],
    ['mushroom', 'Mushroom'],
    ['octopus', 'Octopus'],
    ['bear', 'Bear'],
].map(([key, name]) => ({
    key,
    name,
    file: `avatars/${key}.webp`,
    opensAt: 1,
}));
/** The colour behind a picture (§12a.14.3). All open to everybody. */
export const PROFILE_BACKDROPS = [
    { key: 'violet', name: 'Violet', color: '#7815ff' },
    { key: 'ink', name: 'Ink', color: '#1c1833' },
    { key: 'plum', name: 'Plum', color: '#5b2a86' },
    { key: 'rose', name: 'Rose', color: '#c2416b' },
    { key: 'coral', name: 'Coral', color: '#e8664f' },
    { key: 'amber', name: 'Amber', color: '#e8a23a' },
    { key: 'lime', name: 'Lime', color: '#8fbf3f' },
    { key: 'teal', name: 'Teal', color: '#1f9e8f' },
    { key: 'sky', name: 'Sky', color: '#3a8ee8' },
    { key: 'indigo', name: 'Indigo', color: '#3f4bb8' },
    { key: 'slate', name: 'Slate', color: '#4a5368' },
    { key: 'ivory', name: 'Ivory', color: '#fffef1' },
];
/**
 * The frames a level opens (§12a.6). The three a badge opens — Master, Champion, Rare — have
 * pictures already and join this list with the badges, in A5.
 */
export const PROFILE_FRAMES = [
    {
        key: 'plain',
        name: 'Plain',
        file: 'frames/plain.webp',
        opensAt: 1,
        tint: '#3a3a40',
    },
    {
        key: 'bronze',
        name: 'Bronze',
        file: 'frames/bronze.webp',
        opensAt: 5,
        tint: '#b8683a',
    },
    {
        key: 'silver',
        name: 'Silver',
        file: 'frames/silver.webp',
        opensAt: 10,
        tint: '#b9c0c8',
    },
    {
        key: 'gold',
        name: 'Gold',
        file: 'frames/gold.webp',
        opensAt: 20,
        tint: '#e0a92a',
    },
    {
        key: 'platinum',
        name: 'Platinum',
        file: 'frames/platinum.webp',
        opensAt: 35,
        tint: '#d6e4f0',
    },
    {
        key: 'prism',
        name: 'Prism',
        file: 'frames/prism.webp',
        opensAt: 50,
        tint: '#c4a3ff',
    },
];
/** BoardFlight's own backgrounds (§12a.14.3): two from the start, four a level opens. */
export const PROFILE_BANNERS = [
    { key: 'dusk', name: 'Dusk tiles', file: 'banners/dusk.webp', opensAt: 1 },
    { key: 'dawn', name: 'Dawn meadow', file: 'banners/dawn.webp', opensAt: 1 },
    { key: 'sea', name: 'Tile sea', file: 'banners/sea.webp', opensAt: 10 },
    { key: 'canyon', name: 'Canyon', file: 'banners/canyon.webp', opensAt: 20 },
    { key: 'aurora', name: 'Aurora', file: 'banners/aurora.webp', opensAt: 35 },
    {
        key: 'skycity',
        name: 'Sky city',
        file: 'banners/skycity.webp',
        opensAt: 50,
    },
];
export const DEFAULT_APPEARANCE = {
    avatar: 'meeple',
    backdrop: 'violet',
    frame: 'plain',
    banner: 'dusk',
};
/** Whether `key` is a look of `slot` that `level` opens. */
export function looksOpen(slot, key, level) {
    if (slot === 'backdrop')
        return PROFILE_BACKDROPS.some((b) => b.key === key);
    const list = slot === 'avatar' ? PROFILE_AVATARS : slot === 'frame' ? PROFILE_FRAMES : PROFILE_BANNERS;
    const look = list.find((l) => l.key === key);
    return look !== undefined && level >= look.opensAt;
}
/**
 * What a person wears, read back: each stored choice if it is still open at `level`, else the
 * default for that slot (§12a.9, *a slot whose item is no longer held falls back*). Never throws, so
 * a choice made at a level since lost never breaks a page.
 */
export function wornAppearance(stored, level) {
    const pick = (slot) => {
        const key = stored[slot];
        return key && looksOpen(slot, key, level) ? key : DEFAULT_APPEARANCE[slot];
    };
    return {
        avatar: pick('avatar'),
        backdrop: pick('backdrop'),
        frame: pick('frame'),
        banner: pick('banner'),
    };
}
/* ------------------------------------------------------------------ the wire (§4.3, §12a.12) */
/** How long a link code lives (§4.3). */
export const PLAY_LINK_TTL_MS = 10 * 60_000;
const PlayerHash = z.string().regex(/^[0-9a-f]{64}$/);
/** `POST /v1/play/links` — the layer asks for a code for one seat's per-game key. */
export const PlayLinkRequest = z.object({
    key: z.string().min(1).max(200),
    buildId: Ulid,
    installId: Uuid,
    player: PlayerHash,
});
/** `POST /v1/me/profile/links/:code` — the press. */
export const ConfirmProfileLinkRequest = z
    .object({
    /** Which of the two buttons: *Add to my public profile* or *Add privately*. */
    public: z.boolean(),
    /** *Are you 18 or older?*, asked once (decision 25). */
    adult: z.boolean().optional(),
})
    .strict();
/** `PATCH /v1/me/profile`. Each look is checked against what the level opens now. */
export const PatchProfileRequest = z
    .object({
    publicProfile: z.boolean().optional(),
    showOnLeaderboards: z.boolean().optional(),
    adult: z.boolean().optional(),
    avatar: z.string().max(40).optional(),
    backdrop: z.string().max(40).optional(),
    frame: z.string().max(40).optional(),
    banner: z.string().max(40).optional(),
})
    .strict();
/** A day as a profile says it: `YYYY-MM-DD` in UTC. */
export function profileDay(at) {
    return new Date(at).toISOString().slice(0, 10);
}
//# sourceMappingURL=profile.js.map